import { useState, useEffect, forwardRef } from "react";
import { Link } from "react-router-dom";

const CONSENT_KEY = "heilys_cookie_consent";

export type CookieConsent = {
  necessary: true;
  analytics: boolean;
  timestamp: number;
};

export function readConsent(): CookieConsent | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    // Rückwärtskompatibilität: früherer Wert war Plaintext "accepted"
    if (raw === "accepted") {
      return { necessary: true, analytics: false, timestamp: Date.now() };
    }
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && "analytics" in parsed) {
      return {
        necessary: true,
        analytics: !!parsed.analytics,
        timestamp: parsed.timestamp ?? Date.now(),
      };
    }
  } catch {
    /* ignore */
  }
  return null;
}

function writeConsent(consent: CookieConsent) {
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    window.dispatchEvent(new CustomEvent("heilys:consent-change", { detail: consent }));
  } catch {
    /* ignore */
  }
}

const CookieBanner = forwardRef<HTMLDivElement>((_, ref) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!readConsent()) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const save = (analytics: boolean) => {
    writeConsent({ necessary: true, analytics, timestamp: Date.now() });
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div ref={ref} className="fixed inset-x-0 bottom-0 z-50 p-3 md:p-4">
      <div className="mx-auto flex max-w-3xl flex-col gap-3 rounded-xl border border-border/50 bg-card/95 px-4 py-3 shadow-xl backdrop-blur-lg sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs leading-relaxed text-muted-foreground sm:text-sm">
          Wir nutzen technisch notwendige Cookies. Optional helfen anonyme Reichweitenmessungen, die Seite zu verbessern.{" "}
          <Link to="/datenschutz" className="font-medium text-foreground underline underline-offset-2 hover:text-primary">
            Datenschutz
          </Link>
        </p>
        <div className="flex shrink-0 gap-2">
          <button
            onClick={() => save(false)}
            className="rounded-md border border-border/60 bg-transparent px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted/40"
          >
            Nur notwendig
          </button>
          <button
            onClick={() => save(true)}
            className="rounded-md bg-primary px-4 py-1.5 text-xs font-medium text-primary-foreground transition-colors hover:brightness-110"
          >
            Alle akzeptieren
          </button>
        </div>
      </div>
    </div>
  );
});

CookieBanner.displayName = "CookieBanner";

export default CookieBanner;
